import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../api/client';

interface AuthState {
  isAuthenticated: boolean;
  user: { 
    encrypted_yw_id: string;
    tier: 'business' | 'free' | 'pro';
    is_verified?: boolean;
    email?: string;
    name?: string;
    waiver_accepted?: number;
    avatar_url?: string;
    role?: string;
    plan?: 'visitor' | 'free_trial' | 'free' | 'pro' | 'business';
    trial_ends_at?: number;
  } | null;
  login: (email: string, password?: string) => Promise<void>;
  signup: (data: any) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  acceptWaiver: () => Promise<void>;
  updateTier: (tier: 'business' | 'free' | 'pro', paymentMethodId?: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      login: async (email: string, password?: string) => {
        try {
          // Clear force logout flag on new login attempt
          localStorage.removeItem('forceLoggedOut');

          // Try new auth first
          if (password) {
            const { user } = await api.post('/auth/login', { email, password });
            set({ isAuthenticated: true, user });
            return;
          }

          // Fallback to platform auth check
          const user = await api.get('/me');
          set({ isAuthenticated: true, user });
        } catch (e) {
          console.error('Login failed', e);
          throw e;
        }
      },
      signup: async (data: any) => {
        await api.post('/auth/signup', data);
      },
      logout: async () => {
        try {
          await api.post('/auth/logout', {});
        } catch (e) {
          console.error('Logout API call failed', e);
        }
        
        // Clear all storage
        localStorage.clear();
        sessionStorage.clear();
        
        // Set flag to prevent auto-login
        localStorage.setItem('forceLoggedOut', 'true');
        
        // Clear state
        set({ isAuthenticated: false, user: null });
        
        // Force navigate to landing page
        window.location.href = '/';
      },
      checkAuth: async () => {
        // Prevent auto-login if user was forced logged out
        if (localStorage.getItem('forceLoggedOut')) {
          set({ isAuthenticated: false, user: null });
          return;
        }

        try {
          const user = await api.get('/me');
          set({ isAuthenticated: true, user });
        } catch (e) {
          set({ isAuthenticated: false, user: null });
        }
      },
      acceptWaiver: async () => {
        // Backend call is done in component, just update local state
        const user = get().user;
        if (user) {
          set({ user: { ...user, waiver_accepted: 1 } });
        }
      },
      updateTier: async (tier, paymentMethodId) => {
        // Backend call is done in component, just update local state
        const user = get().user;
        if (user) {
          set({ user: { ...user, tier } });
        }
      }
    }),
    {
      name: 'auth-storage',
    }
  )
);
